import { useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Box, Card, CardContent, Typography, TextField,
  Button, CircularProgress, InputAdornment, IconButton, Stack,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import authFetch from '../utils/authFetch';

const PwdField = ({ label, value, onChange, show, onToggle }) => (
  <TextField
    label={label}
    type={show ? 'text' : 'password'}
    value={value}
    onChange={e => onChange(e.target.value)}
    fullWidth
    InputProps={{
      sx: { direction: 'rtl' },
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={onToggle} edge="end" size="small" aria-label={show ? 'הסתר סיסמה' : 'הצג סיסמה'}>
            {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
);

const ChangePasswordPage = () => {
  const [current, setCurrent]   = useState('');
  const [newPwd, setNewPwd]     = useState('');
  const [confirm, setConfirm]   = useState('');
  const [show, setShow]         = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading]   = useState(false);
  const notify   = useNotify();
  const navigate = useNavigate();

  const toggle = key => setShow(s => ({ ...s, [key]: !s[key] }));

  const mismatch = confirm && newPwd !== confirm;
  const tooShort = newPwd && newPwd.length < 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!current || !newPwd || !confirm) {
      notify('נא למלא את כל השדות', { type: 'warning' }); return;
    }
    if (newPwd !== confirm) {
      notify('הסיסמאות החדשות אינן תואמות', { type: 'error' }); return;
    }
    if (newPwd.length < 8) {
      notify('הסיסמא חייבת להכיל לפחות 8 תווים', { type: 'error' }); return;
    }

    setLoading(true);
    try {
      const res = await authFetch('/auth/change-password', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ currentPassword: current, newPassword: newPwd }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'שגיאה');
      }

      notify('הסיסמא שונתה בהצלחה', { type: 'success' });
      navigate('/');
    } catch (err) {
      notify(err.message || 'שגיאה בשינוי הסיסמא', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="crm-page" p={3} maxWidth={480} mx="auto" dir="rtl">
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <LockResetIcon sx={{ color: '#c8a96e', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700} color="#0d1d3e">שינוי סיסמא</Typography>
      </Stack>

      <Card component="form" onSubmit={handleSubmit}
        sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <PwdField
              label="סיסמא נוכחית"
              value={current}
              onChange={setCurrent}
              show={show.current}
              onToggle={() => toggle('current')}
            />

            <PwdField
              label="סיסמא חדשה"
              value={newPwd}
              onChange={setNewPwd}
              show={show.new}
              onToggle={() => toggle('new')}
            />
            {tooShort && (
              <Typography variant="caption" color="error" sx={{ mt: -1.5 }}>
                לפחות 8 תווים
              </Typography>
            )}

            <PwdField
              label="אימות סיסמא חדשה"
              value={confirm}
              onChange={setConfirm}
              show={show.confirm}
              onToggle={() => toggle('confirm')}
            />
            {mismatch && (
              <Typography variant="caption" color="error" sx={{ mt: -1.5 }}>
                הסיסמאות אינן תואמות
              </Typography>
            )}

            <Stack className="crm-actions" direction="row" spacing={2} justifyContent="flex-end" pt={1}>
              <Button variant="outlined" onClick={() => navigate('/')}
                sx={{ borderColor: '#ccc', color: '#666', borderRadius: 2 }}>
                ביטול
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || mismatch || tooShort}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
                sx={{ backgroundColor: '#0d1d3e', borderRadius: 2, px: 3,
                  '&:hover': { backgroundColor: '#162d5a' } }}
              >
                שמור סיסמא
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChangePasswordPage;
